import { useCallback, useEffect, useRef, useState } from "react";
import { computeTicksBetween, offsetByTicks } from "../../../utils/priceUtils";
import { addEventListenerAllWindows } from "~/utils/popoutWindowsRegistry";

const getEffectivePrice = (entry) => {
  const value = entry?.price ?? entry?.limit ?? entry?.stop;
  return Number.isFinite(value) ? value : undefined;
};

/**
 * Capture a draggable item from one order/leg annotation: its start price and
 * (for a main order) the tick offsets of its OTO legs so they follow along.
 */
const buildDragItem = (annotation, order, symbol) => {
  const isOto = annotation.kind === "loss" || annotation.kind === "profit";
  const mainPrice = getEffectivePrice(order);
  const legDeltas = [];
  if (!isOto && Array.isArray(order.oto) && Number.isFinite(mainPrice)) {
    order.oto.forEach((leg) => {
      let deltaTicks = leg.deltaTicks;
      if (deltaTicks === undefined) {
        const legPrice = getEffectivePrice(leg);
        if (Number.isFinite(legPrice)) {
          deltaTicks = computeTicksBetween(symbol, mainPrice, legPrice);
        }
      }
      if (deltaTicks !== undefined) legDeltas.push({ id: leg.id, deltaTicks });
    });
  }
  const otoOrderId = isOto ? annotation.otoOrderId : null;
  const startPrice = isOto
    ? getEffectivePrice(order.oto?.find((leg) => leg.id === otoOrderId))
    : mainPrice;
  if (!Number.isFinite(startPrice)) return null;
  return { orderId: annotation.orderId, otoOrderId, legDeltas, startPrice };
};

/** priceOverrides map ({ [orderId]: { main?, oto? } }) for items shifted by deltaTicks. */
const buildOverrides = (items, deltaTicks, symbol) => {
  const overrides = {};
  items.forEach((item) => {
    const nextPrice = offsetByTicks(symbol, item.startPrice, deltaTicks);
    if (!Number.isFinite(nextPrice)) return;
    const slot = overrides[item.orderId] || (overrides[item.orderId] = {});
    if (item.otoOrderId) {
      slot.oto = slot.oto || {};
      slot.oto[item.otoOrderId] = nextPrice;
    } else {
      slot.main = nextPrice;
      item.legDeltas.forEach(({ id, deltaTicks: legDelta }) => {
        const legPrice = offsetByTicks(symbol, nextPrice, legDelta);
        if (Number.isFinite(legPrice)) {
          slot.oto = slot.oto || {};
          slot.oto[id] = legPrice;
        }
      });
    }
  });
  return overrides;
};

/**
 * Pointer-based drag for SuperDOM order annotations.
 *
 * Tracks the cursor over ladder rows and exposes a live `dragState.overrides`
 * map ({ [orderId]: { main?, oto? } }) so the panel re-renders the dragged
 * order(s) at their projected prices. The store is untouched mid-drag —
 * `onCommit(moves)` fires once on release.
 *
 * - `startDrag` drags a single order/leg (a main carries its OTO legs).
 * - `startStackDrag` drags every order/leg sharing a price by the same delta.
 *
 * @param {Object}   params
 * @param {string}   params.symbol   - Book symbol (tick math).
 * @param {Array}    params.orders   - Current orders (to resolve legs + deltas).
 * @param {Function} params.onCommit - (moves[]) on release; each move is
 *                                     { orderId, otoOrderId?, price }.
 */
export const useBookOrderDrag = ({ symbol, orders, onCommit }) => {
  const [dragState, setDragState] = useState(null);
  const dragRef = useRef(null);
  const isDragging = dragState !== null;

  const endDrag = useCallback(
    (commit) => {
      const drag = dragRef.current;
      dragRef.current = null;
      setDragState(null);
      if (!drag || !commit) return;
      if (
        !Number.isFinite(drag.lastPrice) ||
        drag.lastPrice === drag.anchorPrice
      )
        return;
      const deltaTicks = computeTicksBetween(
        symbol,
        drag.anchorPrice,
        drag.lastPrice,
      );
      if (!Number.isFinite(deltaTicks) || deltaTicks === 0) return;
      const moves = [];
      drag.items.forEach((item) => {
        const next = offsetByTicks(symbol, item.startPrice, deltaTicks);
        if (!Number.isFinite(next)) return;
        moves.push(
          item.otoOrderId
            ? {
                orderId: item.orderId,
                otoOrderId: item.otoOrderId,
                price: next,
              }
            : { orderId: item.orderId, price: next },
        );
      });
      if (moves.length) onCommit?.(moves);
    },
    [symbol, onCommit],
  );

  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      // PoC #popout: resolve rows in the document of the window the pointer
      // is actually in (clientX/Y are relative to that window's viewport).
      const doc = event.view?.document ?? document;
      const target = doc.elementFromPoint(event.clientX, event.clientY);
      const row = target?.closest("tr[data-bbp-price]");
      if (!row) return;
      const rowPrice = Number(row.dataset.bbpPrice);
      if (!Number.isFinite(rowPrice) || rowPrice === drag.lastPrice) return;
      drag.lastPrice = rowPrice;
      const deltaTicks = computeTicksBetween(
        symbol,
        drag.anchorPrice,
        rowPrice,
      );
      const overrides = Number.isFinite(deltaTicks)
        ? buildOverrides(drag.items, deltaTicks, symbol)
        : {};
      setDragState({ overrides, orderIds: drag.orderIds });
    },
    [symbol],
  );

  const handlePointerUp = useCallback(() => endDrag(true), [endDrag]);
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") endDrag(false);
    },
    [endDrag],
  );

  useEffect(() => {
    if (!isDragging) return undefined;
    // PoC #popout: attach in every window — the drag may run inside a popout,
    // and rows only exist in the book's own document (others no-op).
    const detachers = [
      addEventListenerAllWindows("pointermove", handlePointerMove),
      addEventListenerAllWindows("pointerup", handlePointerUp),
      addEventListenerAllWindows("pointercancel", handlePointerUp),
      addEventListenerAllWindows("keydown", handleKeyDown),
    ];
    return () => detachers.forEach((detach) => detach());
  }, [isDragging, handlePointerMove, handlePointerUp, handleKeyDown]);

  const beginDrag = useCallback((items, anchorPrice) => {
    const valid = items.filter(Boolean);
    if (valid.length === 0 || !Number.isFinite(anchorPrice)) return;
    const orderIds = [...new Set(valid.map((item) => item.orderId))];
    dragRef.current = {
      items: valid,
      orderIds,
      anchorPrice,
      lastPrice: anchorPrice,
    };
    setDragState({ overrides: null, orderIds });
  }, []);

  // Drag a single order/leg from its moldura.
  const startDrag = useCallback(
    (event, annotation) => {
      if (!annotation?.orderId || event.button !== 0) return;
      const order = orders.find((o) => o.id === annotation.orderId);
      if (!order) return;
      event.preventDefault();
      event.stopPropagation();
      const item = buildDragItem(annotation, order, symbol);
      if (item) beginDrag([item], item.startPrice);
    },
    [orders, symbol, beginDrag],
  );

  // Drag every order/leg stacked at one price together (no preventDefault so
  // a plain click still falls through to open the stack panel).
  const startStackDrag = useCallback(
    (event, price, annotations) => {
      if (
        event.button !== 0 ||
        !Array.isArray(annotations) ||
        annotations.length === 0
      )
        return;
      const items = annotations
        .map((annotation) => {
          const order = orders.find((o) => o.id === annotation.orderId);
          return order ? buildDragItem(annotation, order, symbol) : null;
        })
        .filter(Boolean);
      if (items.length) beginDrag(items, price);
    },
    [orders, symbol, beginDrag],
  );

  return { dragState, startDrag, startStackDrag, isDragging };
};
